import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import "./Contact.css";

function Contact() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = 'Contact Us | Sapium Beauty - Book Consultation London';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contact Sapium Beauty to book your free consultation at our East London clinic (28 Barking Rd, London E16 1EQ). Call or email us today.');
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Contact Us | Sapium Beauty - Book Consultation London</title>
        <meta
          name="description"
          content="Contact Sapium Beauty to book your free consultation at our East London clinic (28 Barking Rd, London E16 1EQ). Call or email us today."
        />
        <link rel="canonical" href="https://sapiumbeauty.com/contact" />
      </Helmet>
      <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-hero-content">
          <h1 className="contact-hero-title">{t('contact.title')}</h1>
          <p className="contact-hero-subtitle">{t('contact.subtitle')}</p>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="contact-info-section">
        <div className="contact-info-container">
          <div className="contact-info-card">
            <div className="contact-info-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
              </svg>
            </div>
            <h3>{t('contact.emailLabel')}</h3>
            <a href="mailto:admin@sapiumbeauty.com" className="contact-link">{t('contact.email')}</a>
          </div>
          
          <div className="contact-info-card">
            <div className="contact-info-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="currentColor"/>
              </svg>
            </div>
            <h3>{t('contact.phoneLabel')}</h3>
            <a href="tel:+447577151625" className="contact-link">{t('contact.phone')}</a>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="locations-section">
        <div className="locations-container">
          <div className="location-card">
            <div className="location-header">
              <h2 className="location-title">{t('contact.mainTreatmentsTitle')}</h2>
              <div className="location-address">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
                </svg>
                <p>{t('contact.mainTreatmentsAddress')}</p>
              </div>
            </div>
            <div className="location-map">
              <iframe
                title="Clinic Location - East London"
                src="https://www.google.com/maps?q=28+Barking+Rd,+London+E16+1EQ&output=embed"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section className="booking-section" id="book-appointment">
        <div className="booking-container">
          <h2 className="booking-title">{t('contact.bookAppointment')}</h2>
          <p className="booking-subtitle">{t('contact.bookAppointmentSubtitle')}</p>
          <a 
            href="https://www.fresha.com/book-now/sapium-beauty-hu8f8ild/all-offer?share=true&pId=2743251" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="booking-button"
          >
            {t('services.cta.button')}
          </a>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="social-section">
        <div className="social-container">
          <h2 className="social-title">{t('contact.followUs')}</h2>
          <div className="social-icons">
            <a 
              href="https://www.instagram.com/iryna_laser_glow/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="social-icon-link"
              aria-label="Instagram"
            >
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="24" height="24" rx="6" fill="#fff"/>
                <path d="M16.98 2H7.02C4.25 2 2 4.25 2 7.02v9.96C2 19.75 4.25 22 7.02 22h9.96C19.75 22 22 19.75 22 16.98V7.02C22 4.25 19.75 2 16.98 2zM20 16.98c0 1.66-1.36 3.02-3.02 3.02H7.02A3.02 3.02 0 0 1 4 16.98V7.02C4 5.36 5.36 4 7.02 4h9.96C18.64 4 20 5.36 20 7.02v9.96z" fill="#E1306C"/>
                <path d="M12 7.2A4.8 4.8 0 1 0 12 16.8 4.8 4.8 0 0 0 12 7.2zm0 7.8A3 3 0 1 1 12 9a3 3 0 0 1 0 6zm4.2-7.32a1.08 1.08 0 1 0 0 2.16 1.08 1.08 0 0 0 0-2.16z" fill="#E1306C"/>
              </svg>
            </a>
            <a 
              href="https://www.facebook.com/profile.php?id=61567286369269" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="social-icon-link"
              aria-label="Facebook"
            >
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="24" height="24" rx="6" fill="#fff"/>
                <path d="M17 2H7C4.24 2 2 4.24 2 7v10c0 2.76 2.24 5 5 5h10c2.76 0 5-2.24 5-5V7c0-2.76-2.24-5-5-5zm1 7h-2c-.55 0-1 .45-1 1v2h3l-.5 3h-2.5v7h-3v-7H8v-3h2V9c0-1.66 1.34-3 3-3h2v3z" fill="#1877F3"/>
              </svg>
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}

export default Contact;
